/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * InquiryInfo Page attached files component
 *
 * @author Julia Ignatieva
 * @version 1.15.0
 * @since 1.15.0
 */

import { useCallback } from 'react';

import moment from 'moment';

import { icons } from '../../../assets';
import { LinkBlock } from '../../../components/common';
import { ImgStyled } from '../../../components/common/Block/Img';
import { ELinkTarget } from '../../../components/common/ExternalLink/ExternalLink';
import { getTimeZone } from '../../../components/Table/helpers/getTimeZone';
import { API_INQUIRY_FILE_DOWNLOAD, API_INQUIRY_FILE_PARAM, API_INQUIRY_ID_PARAM } from '../../../modules/app/Api';
import { EFileExtension, MapExtensionToIcon } from '../../../modules/app/appConstants';
import { IPageInquiryInfoTranslation } from '../../../modules/internationalization/types/pages/inquiryInfo';
import { getFileExtension } from '../../../utils/formatText';
import { stringFormatParams } from '../../../utils/stringFormat';
import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import { IInquiryAttachedFile } from '../types/inquiryInfoType';
import {
    InquiryInfoPageAttachedFileContainerStyled,
    InquiryInfoPageAttachedFileNameStyled,
    InquiryInfoPageStyled,
    InquiryInfoPageAttachedFileInfoStyled,
    InquiryInfoPageAttachedFileUserInfoStyled,
    InquiryInfoPageDateInfoStyled,
} from './styled/InquiryInfoPageStyled';

export interface IInquiryInfoAttachedFiles {
    files: IInquiryAttachedFile[];
    filesCount: number;
    inquiryId: string;
    translationInquiryInfo: IPageInquiryInfoTranslation;
}

const InquiryInfoPageAttachedFiles = (props: IInquiryInfoAttachedFiles) => {
    const {
        files,
        filesCount,
        inquiryId,
        translationInquiryInfo,
    } = props;

    const getFileDownloadUrl = useCallback((file: IInquiryAttachedFile) => {
        return stringFormatParams(API_INQUIRY_FILE_DOWNLOAD, {
            [API_INQUIRY_ID_PARAM]: inquiryId,
            [API_INQUIRY_FILE_PARAM]: file.id,
        });
    }, [inquiryId]);

    return (
        <InquiryInfoPageStyled>
            <div className="inquiry-info-page__title">
                {translationInquiryInfo.attachedFiles.title}
            </div>
            {filesCount === 0 &&
                <div className="inquiry-info-page__subtitle">
                    {translationInquiryInfo.attachedFiles.noFilesInfo}
                </div>
            }
            {filesCount > 0 && files.map((item, index) => (
                <div key={item.name + index}>
                    <LinkBlock
                        isExternalUrl
                        target={ELinkTarget.BLANK}
                        url={getFileDownloadUrl(item)}
                    >
                        <InquiryInfoPageAttachedFileContainerStyled key={item.name}>
                            <ImgStyled
                                className="medium"
                                src={MapExtensionToIcon[getFileExtension(item.name) as EFileExtension]
                                ?? icons.defaultFileIcon}
                            />
                            <InquiryInfoPageAttachedFileNameStyled>
                                {item.name}
                            </InquiryInfoPageAttachedFileNameStyled>
                        </InquiryInfoPageAttachedFileContainerStyled>
                    </LinkBlock>
                    <InquiryInfoPageAttachedFileInfoStyled>
                        <InquiryInfoPageAttachedFileUserInfoStyled>
                            {translationInquiryInfo.userType[item.createUser]},&nbsp;
                        </InquiryInfoPageAttachedFileUserInfoStyled>
                        <InquiryInfoPageDateInfoStyled>
                            {moment(item.createDate).format(EFormatData.FullDateWithTime)}&nbsp;
                            {getTimeZone(item.createDate)}
                        </InquiryInfoPageDateInfoStyled>
                    </InquiryInfoPageAttachedFileInfoStyled>
                </div>
            ))}
        </InquiryInfoPageStyled>
    );
};

export default InquiryInfoPageAttachedFiles;
